import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, AppThunk } from "../../app/store";
import { fetchFilter } from "./filterAPI";

export function filterToQueryString(filter: Filter): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) {
        if (value === null || value === undefined) continue;
        // arrays → multiple params: ?labels=foo&labels=bar
        if (Array.isArray(value)) {
            if (value.length === 0) continue;
            value.forEach((v) => params.append(key, v));
            continue;
        }
        // booleans → keep only true, or include both?
        // Here we include both true and false, but you can skip false if you want.
        if (typeof value === "boolean") {
            params.set(key, value ? "true" : "false");
            continue;
        }
        // strings → skip empty
        if (typeof value === "string" && value.trim() === "") continue;
        params.set(key, String(value));
    }
    return params.toString(); // example: "page_id=107&text=value"
}

export interface Filter {
    id: string | null;
    name: string | null;
    search_text: string | null;
    in_content: boolean;
    kprivate: boolean;
    planned_start_min: string | null;
    planned_start_max: string | null;
    planned_end_min: string | null;
    planned_end_max: string | null;
    end_is_null: boolean;
    contexts_or: string[];
    tags_or: string[];
    labels_and: string[];
    labels_or: string[];
    parent_id: string | null;
    page_id: string | null;
    contact_id: string | null;
    level_1: boolean; // area
    level_2: boolean; // space
    level_3: boolean; // project
    level_4: boolean; // epic
    level_5: boolean; // deliverable
    level_6: boolean; // task
    in_tickets: boolean;
    in_pages: boolean;
    in_files: boolean;
    //space
    //area
}

export interface FilterForm {
    id: string | null;
    name: string | null;
    search_text: string | null;
    in_content: boolean;
    kprivate: boolean;
    planned_start_min: string | null;
    planned_start_max: string | null;
    planned_end_min: string | null;
    planned_end_max: string | null;
    end_is_null: boolean;
    contexts_or: string[];
    tags_or: string[];
    labels_and: string[];
    labels_or: string[];
    parent_id: string | null;
    page_id: string | null;
    contact_id: string | null;
    level_1: boolean; // area
    level_2: boolean; // space
    level_3: boolean; // project
    level_4: boolean; // epic
    level_5: boolean; // deliverable
    level_6: boolean; // task
    in_tickets: boolean;
    in_pages: boolean;
    in_files: boolean;
    //area
}

export function toForm(f: Filter) {
    return {
        id: f.id,
        name: f.name,
        search_text: f.search_text,
        in_content: f.in_content,
        kprivate: f.kprivate,
        planned_start_min: f.planned_start_min,
        planned_start_max: f.planned_start_max,
        planned_end_min: f.planned_end_min,
        planned_end_max: f.planned_end_max,
        end_is_null: f.end_is_null,
        contexts_or: f.contexts_or,
        tags_or: f.tags_or,
        labels_and: f.labels_and,
        labels_or: f.labels_or,
        parent_id: f.parent_id,
        page_id: f.page_id,
        contact_id: f.contact_id,
        level_1: f.level_1, // area
        level_2: f.level_2, // space
        level_3: f.level_3, // project
        level_4: f.level_4, // epic
        level_5: f.level_5, // deliverable
        level_6: f.level_6, // task
        in_tickets: f.in_tickets,
        in_pages: f.in_pages,
        in_files: f.in_files,
    };
}

export function newFilter(): Filter {
    return {
        id: null,
        name: null,
        search_text: null,
        kprivate: true,
        in_content: false,
        planned_start_min: null,
        planned_start_max: null,
        planned_end_min: null,
        planned_end_max: null,
        end_is_null: true,
        contexts_or: [],
        tags_or: [],
        labels_and: [],
        labels_or: [],
        parent_id: null,
        page_id: null,
        contact_id: null,
        level_1: false, // area
        level_2: false, // space
        level_3: false, // project
        level_4: true, // epic
        level_5: true, // deliverable
        level_6: false, // task
        in_tickets: true,
        in_pages: false,
        in_files: false,
    };
}
export function fromText(text: string): Filter {
    return {
        id: null,
        name: null,
        search_text: text,
        in_content: false,
        kprivate: false,
        planned_start_min: null,
        planned_start_max: null,
        planned_end_min: null,
        planned_end_max: null,
        end_is_null: true,
        contexts_or: [],
        tags_or: [],
        labels_or: [],
        labels_and: [],
        parent_id: null,
        page_id: null,
        contact_id: null,
        level_1: false, // area
        level_2: true, // space
        level_3: true, // project
        level_4: true, // epic
        level_5: false, // deliverable
        level_6: false, // task
        in_tickets: true,
        in_pages: false,
        in_files: false,
    };
}

export function specific(
    tags_or: string[],
    level3: boolean,
    level4: boolean,
    level5: boolean,
): FilterForm {
    let filter = newFilter();
    //filter.level_4 = true;
    filter.level_3 = level3;
    filter.level_4 = level4;
    filter.level_5 = level5;
    filter.tags_or = tags_or;
    return filter;
}

export interface FilterState {
    value: number;
    status: "idle" | "loading" | "failed";
}

const initialState: FilterState = {
    value: 0,
    status: "idle",
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const incrementAsync = createAsyncThunk(
    "filter/fetchFilter",
    async (amount: number) => {
        const response = await fetchFilter(amount);
        // The value we return becomes the `fulfilled` action payload
        return response.data;
    },
);

export const filterSlice = createSlice({
    name: "filter",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        increment: (state) => {
            // Redux Toolkit allows us to write "mutating" logic in reducers. It
            // doesn't actually mutate the state because it uses the Immer library,
            // which detects changes to a "draft state" and produces a brand new
            // immutable state based off those changes
            state.value += 1;
        },
        decrement: (state) => {
            state.value -= 1;
        },
        // Use the PayloadAction type to declare the contents of `action.payload`
        incrementByAmount: (state, action: PayloadAction<number>) => {
            state.value += action.payload;
        },
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(incrementAsync.pending, (state) => {
                state.status = "loading";
            })
            .addCase(incrementAsync.fulfilled, (state, action) => {
                state.status = "idle";
                state.value += action.payload;
            })
            .addCase(incrementAsync.rejected, (state) => {
                state.status = "failed";
            });
    },
});

export const { increment, decrement, incrementByAmount } = filterSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectFilter = (state: RootState) => state.filter.value;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const incrementIfOdd =
    (amount: number): AppThunk =>
    (dispatch, getState) => {
        const currentValue = selectFilter(getState());
        if (currentValue % 2 === 1) {
            dispatch(incrementByAmount(amount));
        }
    };

export default filterSlice.reducer;
